import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnDestroy, OnInit, inject } from "@angular/core";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartDialogService, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { BusyIndicatorComponent } from "@tylertech/common-components-module";
import { LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { AccountDetailCardComponent } from "@tylertech/general-ledger-module";
import { AccountDetailViewModel, IChartCriteria, SettingLookupOption, } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "project-accounting-project-budget-recap-chart",
    templateUrl: "./projectBudgetRecapChart.tmpl.html",
    imports: [
        AccountDetailCardComponent,
        BusyIndicatorComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
    ]
})
export class ProjectBudgetRecapChartComponent implements OnDestroy, OnInit
{
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartDialogService = inject(ChartDialogService);

    private readonly destructor$ = new Subject<void>();

    @Input("chartId")
    private readonly _chartId: string;

    private _chartCriteria: IChartCriteria;
    private project: SettingLookupOption;
    isLoaded: boolean;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    get chartData(): AccountDetailViewModel[]
    {
        return this.hasData() ? this.chartCriteria.ChartDatas[0].Data[0] : [];
    }

    get showChart(): boolean
    {
        return !!this.chartCriteria?.ErrorMessage || this.hasData();
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartTitle(): string
    {
        return "Project Budget Recap";
    }

    get ProjectLabel(): string
    {
        return this.project?.Display;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.setProjectFromCriteria();

        new Promise(() =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return (
            this.chartCriteria?.ChartDatas?.length > 0 &&
            !!this.chartCriteria?.Parameters?.find(parameter => parameter.Id === "BudgetRecapByProject")?.Value
        );
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService
            .GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                const parm = this.chartCriteria?.Parameters?.find(x => x.Id === "BudgetRecapByProject");
                if (parm)
                {
                    this.project = parm.Options.filter(x => x.Value === parm.Value)[0];
                }
                this.loadChartData();
            });
    }

    private setProjectFromCriteria(): void
    {
        this.project = undefined;
        const parm = this.chartCriteria?.Parameters?.find(x => x.Id === "BudgetRecapByProject");
        if (parm)
        {
            this.project = parm.Options.find(x => x.Value === parm.Value);
        }
    }
}
